/**
 * RD-EST-RESP-001 — the CLIENT-side reader for customer-response provenance.
 *
 * One module, two exports, and every in-app surface that wants to say "who
 * answered this estimate, and how" must go through them. It exists because the
 * naive read — `response_source IS NOT NULL` — is WRONG in a way that
 * fabricates an e-signature on screen, and the wrongness is invisible in every
 * ordinary test fixture (see the Return-to-Sent case pinned below).
 *
 * *** LOCKSTEP — THREE COPIES, ONE RULE. ***
 * This file is the JS mirror of:
 *   • `supabase/functions/_shared/estimateResponseState.ts`
 *     (`STANDING_RESPONSE_STATUSES` + `hasStandingCustomerResponse`) — the TS
 *     counterpart, and the file the migration's column COMMENT names.
 *   • `public.estimate_response_is_standing(status, response_at,
 *     response_superseded_at)` — migration 20260831120000 section 1B, the ONE
 *     SQL definition, CALLED (never restated) by the `response_source` column
 *     comment and by `revoke_estimate_response_link`.
 * CHANGE ALL THREE IN ONE COMMIT. The status set already forked once — Phase 2
 * widened it in TypeScript while two SQL copies kept the narrow list — which is
 * the two-doors failure class applied to a READ rule.
 *
 * NOTHING HERE IS AN AUTHORIZATION DECISION. It decides what to DISPLAY. The
 * boundary is the RLS policy plus the edge function's signed token.
 */

/**
 * The four statuses that mean "this estimate has been ANSWERED".
 *
 * `invoiced` / `partially_invoiced` are in the set on purpose: the legal
 * transition map is `accepted -> {sent, invoiced, partially_invoiced}`, so
 * billing converting a genuinely customer-signed quote into an invoice must NOT
 * erase the signature from the preview — that is precisely when the evidence
 * carries the most weight, and it is why the DB's immutability latch stays
 * armed through `invoiced` too.
 *
 * Written out longhand rather than derived, so a diff against the TS/SQL twins
 * is a plain text compare.
 */
export const STANDING_RESPONSE_STATUSES = Object.freeze([
  'accepted', 'declined', 'invoiced', 'partially_invoiced',
]);

/** Statuses a STAFF member can literally mark in-app. See describeEstimateResponse. */
const STAFF_MARKABLE_STATUSES = Object.freeze(['accepted', 'declined']);

function asString(value) {
  return typeof value === 'string' ? value : '';
}

/**
 * THE CANONICAL STANDING-RESPONSE PREDICATE for the client — "does a customer
 * response currently STAND on this row?"
 *
 * *** NEVER gate on `response_source !== null`, and never on status alone. ***
 * "Return to Sent" deliberately KEEPS the stale response record (Guard A
 * forbids a client from clearing it) and only stamps `response_superseded_at`.
 * A staff member can then mark the revived estimate accepted MANUALLY, and the
 * row ends up `status = 'accepted'` still carrying someone else's typed name.
 * Reading `response_source` there renders "Approved online — signed "Maria
 * González"" over a decision Maria never made — a fabricated e-signature on the
 * READ side, reachable through the ordinary admin flow with no forgery and no
 * extra privilege (migration round-2 finding C3). The strict `>` comparison
 * below is the whole difference.
 *
 * Equal timestamps read FALSE deliberately (the SQL twin's T20(c) pins the same
 * boundary): a supersede stamped at the same instant as the response it
 * supersedes is not evidence that the response survived it.
 */
export function hasStandingCustomerResponse(estimate) {
  const row = estimate || {};
  if (!STANDING_RESPONSE_STATUSES.includes(asString(row.status))) return false;
  const respondedAt = asString(row.response_at);
  if (!respondedAt) return false;
  const supersededAt = asString(row.response_superseded_at);
  if (!supersededAt) return true;
  const responded = Date.parse(respondedAt);
  const superseded = Date.parse(supersededAt);
  // An unparseable pair must not read as standing — evidence we cannot order is
  // not evidence.
  if (Number.isNaN(responded) || Number.isNaN(superseded)) return false;
  return responded > superseded;
}

/**
 * @typedef {Object} EstimateResponseDescription
 * @property {'online'|'manual'|'none'} kind
 *   `online` — a STANDING customer response (the public page).
 *   `manual` — accepted/declined by staff in-app ("Marked by you").
 *   `none`   — nothing truthful to say; render nothing.
 * @property {'accepted'|'declined'|null} decision
 * @property {string|null} signedName      typed e-signature (online accepts only)
 * @property {string|null} respondedAt     ISO instant (online only; timestamptz)
 * @property {string|null} declineReason   canned reason token (declines only)
 * @property {string|null} declineReasonNote
 */

/**
 * The ONE derivation of everything a provenance surface needs. Consumers render
 * it; they never re-derive from the raw columns (that is how the
 * `response_source` shortcut spreads).
 *
 * TWO ASYMMETRIES, both deliberate:
 *
 * 1. The ONLINE branch covers all four standing statuses; the MANUAL branch
 *    covers only `accepted` / `declined`. An `invoiced` estimate with no
 *    standing response was never "marked" by anybody — billing simply converted
 *    it — so there is nothing honest to say and we say nothing.
 *
 * 2. The online decision is read from the EVIDENCE columns, not from `status`.
 *    A standing response on an `invoiced` row is an ACCEPT whose status no
 *    longer says so, and `buildEstimateResponsePatch` guarantees the two
 *    branches are mutually exclusive (accept nulls the decline fields; decline
 *    nulls the signature). If neither evidence field is present we return
 *    `none` rather than guess — mislabelling an e-signature is worse than an
 *    absent line.
 */
export function describeEstimateResponse(estimate) {
  const row = estimate || {};
  const empty = {
    kind: 'none',
    decision: null,
    signedName: null,
    respondedAt: null,
    declineReason: null,
    declineReasonNote: null,
  };
  const status = asString(row.status);

  if (hasStandingCustomerResponse(row)) {
    const signedName = asString(row.response_signed_name).trim();
    const declineReason = asString(row.decline_reason).trim();
    const declinedAt = asString(row.declined_at).trim();
    if (signedName) {
      return {
        ...empty,
        kind: 'online',
        decision: 'accepted',
        signedName,
        respondedAt: asString(row.response_at) || null,
      };
    }
    if (declineReason || declinedAt) {
      return {
        ...empty,
        kind: 'online',
        decision: 'declined',
        respondedAt: asString(row.response_at) || null,
        declineReason: declineReason || null,
        declineReasonNote: asString(row.decline_reason_note).trim() || null,
      };
    }
    return empty;
  }

  if (STAFF_MARKABLE_STATUSES.includes(status)) {
    return {
      ...empty,
      kind: 'manual',
      decision: status,
      declineReason: status === 'declined'
        ? (asString(row.decline_reason).trim() || null)
        : null,
      declineReasonNote: status === 'declined'
        ? (asString(row.decline_reason_note).trim() || null)
        : null,
    };
  }

  return empty;
}

/**
 * Is a customer-response link plausibly OUTSTANDING for this estimate — i.e. is
 * revoking it a meaningful action?
 *
 * Two conditions, both required (RD-CX-024, founder decision 2026-10-03):
 *
 *  1. `sent_count > 0` — the link is minted inside `send-estimate-email`, so an
 *     estimate that has never been emailed cannot have one in anybody's inbox.
 *
 *  2. `status === 'sent'` — the ONLY status in which the public page lets the
 *     customer act (`resolveEstimatePageState`: pending/disabled only for
 *     `sent`; accepted/declined/invoiced → responded, expired → expired,
 *     draft/sending → withdrawn, all view-only). Revoking exists to stop an
 *     ANSWER — a wrong inbox accepting on the customer's behalf, or an answer
 *     to a scope/price you are about to change. In every other status nothing
 *     on the link can be done, and the email already carried the PDF, so
 *     revoking protects nothing there. The earlier "every status" rule
 *     (RD-EST-RESP-001) is retired. A link is also NOT auto-revoked on an
 *     answer: it stays the customer's receipt of what they approved. When a
 *     row returns to `sent` (extend expiration, return to Sent) the entry
 *     comes back with it.
 *
 * *** DELIBERATELY NOT gated on the tenant's `customer_estimate_responses`
 * toggle, and the reason is stronger than "the toggle can be flipped back". ***
 * With the feature OFF the endpoint does not 404 the link — `handlers.ts` step 7
 * answers `buildFullView(admin, ctx, 'disabled')`, i.e. it still serves the FULL
 * estimate document: customer name, line items, total. Only the response BUTTONS
 * are withheld. So a disabled tenant's outstanding links are still a live data
 * exposure, and disabling the feature is a plausible FIRST REACTION to
 * discovering a link reached the wrong inbox. Gating this on the toggle would
 * therefore remove the kill switch at the exact moment it is reached for.
 *
 * The cost of that choice is accepted and visible: a tenant that has never
 * enabled the feature has no link to revoke, yet still sees the menu entry on
 * every emailed `sent` estimate (pressing it bumps a version nothing reads and
 * writes one audit row — inert, not harmful). Hiding it for those tenants needs a
 * signal the client does not have: `send-estimate-email` mints the link without
 * stamping the row, so "a link was issued for THIS estimate" is unknowable here.
 * `response_link_version > 1 || response_source` was considered as a proxy and
 * REJECTED — it is false for exactly the enabled-then-disabled case above.
 *
 * Archived rows are excluded upstream (the archived-scope branch of
 * buildEstimateSecondaryActions returns before this is consulted).
 */
export function canRevokeEstimateResponseLink(estimate) {
  const row = estimate || {};
  if (String(row.status || '').toLowerCase() !== 'sent') return false;
  const sentCount = Number(row.sent_count);
  return Number.isFinite(sentCount) && sentCount > 0;
}

/**
 * RD-CX-025 — is the customer's response link CURRENTLY revoked?
 * Revoked = `response_link_revoked_at` set AND (never sent OR revoked after the
 * last send). A re-send stamps `sent_at`, which re-enables the link. Equal
 * timestamps are NOT revoked; an unparseable revoked_at (or sent_at alongside a
 * set revoked_at) fails CLOSED = revoked. TS twin:
 * supabase/functions/_shared/estimateLinkRevoked.ts — change both together.
 */
export function isEstimateResponseLinkRevoked(estimate) {
  const row = estimate || {};
  const raw = row.response_link_revoked_at;
  if (raw === null || raw === undefined || raw === '') return false;
  const revokedMs = Date.parse(String(raw));
  if (!Number.isFinite(revokedMs)) return true;
  const sentRaw = row.sent_at;
  if (sentRaw === null || sentRaw === undefined || sentRaw === '') return true;
  const sentMs = Date.parse(String(sentRaw));
  if (!Number.isFinite(sentMs)) return true;
  return revokedMs > sentMs;
}

/**
 * An optimistic client timestamp that is guaranteed LATER than `otherIso`.
 * The server stamps `sent_at` / `response_link_revoked_at` from its own clock;
 * a phone whose clock runs behind would otherwise write an optimistic value
 * that loses the comparison above and leave the menu in the wrong state until
 * the server value arrives (billing has no realtime). The server value still
 * replaces it on the next load.
 */
export function isoNowAfter(otherIso) {
  const now = Date.now();
  const other = otherIso ? Date.parse(String(otherIso)) : NaN;
  return new Date(Number.isFinite(other) ? Math.max(now, other + 1) : now).toISOString();
}
